import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Crown, ExternalLink, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { SettingsSectionCard } from "@/components/layout/SettingsSectionCard";
import { ProUpgradeFunnel } from "@/components/molecules/ProUpgradeFunnel";
import { PRO_FEATURES } from "@/constants/pro-features";
import { useProAccess } from "@/hooks/useProAccess";
import { billingService } from "@/services/api/billing.service";
import {
  trackCheckoutCanceled,
  trackPurchase,
} from "@/services/firebase/analytics.service";
import { Button } from "@/components/atoms";

export function BillingSettings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isPro, subscription, isLoading, refetch } = useProAccess();
  const [portalLoading, setPortalLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    const checkout = searchParams.get("checkout");
    if (checkout === "success") {
      const storedInterval = sessionStorage.getItem("burnchart_checkout_interval");
      const interval = storedInterval === "year" ? "year" : "month";
      void trackPurchase(interval);
      sessionStorage.removeItem("burnchart_checkout_interval");
      setMessage("Payment received. Your Pro features will unlock shortly.");
      void refetch();
      searchParams.delete("checkout");
      setSearchParams(searchParams, { replace: true });
    } else if (checkout === "canceled") {
      void trackCheckoutCanceled("billing");
      setMessage("Checkout canceled. You can upgrade anytime.");
      searchParams.delete("checkout");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, refetch]);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const session = await billingService.createPortalSession();
      window.location.assign(session.url);
    } catch (error) {
      console.error(error);
      setMessage("Could not open the billing portal. Please try again.");
    } finally {
      setPortalLoading(false);
    }
  };

  if (isLoading) {
    return (
      <SettingsSectionCard
        title="Billing"
        description="Manage your BurnChart Pro subscription."
      >
        <div className="flex items-center gap-2 text-sm text-white/60">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading subscription…
        </div>
      </SettingsSectionCard>
    );
  }

  return (
    <div className="space-y-6">
      {message ? (
        <div className="rounded-[14px] border border-brand/30 bg-brand/10 px-4 py-3 text-sm text-brand">
          {message}
        </div>
      ) : null}

      <SettingsSectionCard
        title="BurnChart Pro"
        description="Multiple startups, AI advisor, business plan generation, and API access."
      >
        {isPro ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-brand">
              <Crown className="h-5 w-5" />
              <span className="font-semibold text-white">Pro is active</span>
            </div>

            <dl className="grid gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-white/45">Billing interval</dt>
                <dd className="font-medium text-white">
                  {subscription?.interval === "year" ? "Yearly" : "Monthly"}
                </dd>
              </div>
              <div>
                <dt className="text-white/45">Status</dt>
                <dd className="font-medium capitalize text-white">
                  {subscription?.status ?? "active"}
                </dd>
              </div>
              {subscription?.currentPeriodEnd ? (
                <div>
                  <dt className="text-white/45">
                    {subscription.cancelAtPeriodEnd
                      ? "Access until"
                      : "Renews on"}
                  </dt>
                  <dd className="font-medium text-white">
                    {format(new Date(subscription.currentPeriodEnd), "MMM d, yyyy")}
                  </dd>
                </div>
              ) : null}
            </dl>

            {subscription?.cancelAtPeriodEnd ? (
              <p className="text-sm text-warn">
                Your subscription will not renew at the end of the current period.
              </p>
            ) : null}

            <Button
              type="button"
              variant="secondary"
              size="md"
              onClick={openPortal}
              disabled={portalLoading}
            >
              {portalLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Opening portal…
                </>
              ) : (
                <>
                  Manage subscription
                  <ExternalLink className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        ) : (
          <ProUpgradeFunnel variant="hero" />
        )}
      </SettingsSectionCard>

      <SettingsSectionCard
        title="What's included in Pro"
        description="Everything below is included with an active subscription."
      >
        <ul className="space-y-3">
          {PRO_FEATURES.map((feature) => (
            <li
              key={feature.id}
              className="rounded-[14px] bg-surface/20 px-4 py-3 text-sm"
            >
              <div className="font-semibold text-white">{feature.title}</div>
              <div className="mt-0.5 text-white/55">{feature.description}</div>
            </li>
          ))}
        </ul>
      </SettingsSectionCard>
    </div>
  );
}
